import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, LogOut, Menu, X, FileText, Shield } from 'lucide-react';
import logo from '../assets/logo.svg';
import { supabase } from '../supabaseClient';
import { toast } from 'sonner';

interface LayoutProps {
	children: React.ReactNode;
	onLogout?: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, onLogout }) => {
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const navigate = useNavigate();

	const navigation = [
		{ name: 'Verifications', href: '/verifications', icon: Shield },
		{ name: 'Submissions', href: '/dashboard', icon: LayoutDashboard },
		{ name: 'Users', href: '/users', icon: Users },
		{ name: 'Logs', href: '/logs', icon: FileText },
	];

	const handleLogout = async () => {
		console.log('Sign out button clicked');
		await supabase.auth.signOut();
		toast.success('Signed out successfully!');
		navigate('/admin'); // or wherever your login page is
	};

	return (
		<div className="min-h-screen bg-gray-50 flex">
			{/* Mobile sidebar backdrop */}
			{sidebarOpen && (
				<div
					className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
					onClick={() => setSidebarOpen(false)}
				/>
			)}

			{/* Sidebar - Completely fixed */}
			<div
				className={`
	              fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0
	              ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
	          `}
			>
				<div className="flex flex-col h-screen sticky top-0">
					{/* Logo/Header */}
					<div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 shrink-0">
						<div className="flex items-center gap-3">
							<div className="flex items-center justify-center">
								<img src={logo} alt="GhanaID Verify Logo" className="w-50 h-20" />
							</div>
						</div>
						<button
							className="lg:hidden p-1 rounded-md hover:bg-gray-100"
							onClick={() => setSidebarOpen(false)}
						>
							<X className="w-5 h-5 text-gray-500" />
						</button>
					</div>

					{/* Navigation - Scrollable if content exceeds height */}
					<nav className="flex-1 px-1 py-6 space-y-2 overflow-y-auto">
						{navigation.map(item => (
							<NavLink
								key={item.name}
								to={item.href}
								className={({ isActive }) => `
	                                  flex items-center gap-3 px-3 py-4 rounded-lg text-sm font-medium transition-colors
	                                  ${
							isActive
								? 'bg-red-50 text-brand-orange border-r-2'
								: 'text-gray-700 hover:bg-gray-100'
							}
	                              `}
							>
								<item.icon className="w-3 h-3" />
								{item.name}
							</NavLink>
						))}
					</nav>

					{/* Footer - Fixed at bottom */}
					<div className="p-4 border-t border-gray-200 shrink-0">
						<button
							onClick={handleLogout}
							className="flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium text-brand-orange rounded-lg hover:bg-gray-100 transition-colors"
						>
							<LogOut className="w-4 h-4" />
							Sign Out
						</button>
					</div>
				</div>
			</div>

			{/* Main content - Scrollable */}
			<div className="flex-1 flex flex-col overflow-hidden lg:ml-64">
				{/* Mobile header */}
				<div className="lg:hidden bg-white shadow-sm border-b border-gray-200">
					<div className="flex items-center justify-between h-16 px-4">
						<button
							onClick={() => setSidebarOpen(true)}
							className="p-2 rounded-md hover:bg-gray-100"
						>
							<Menu className="w-5 h-5 text-gray-500" />
						</button>
						<h1 className="text-lg font-semibold text-gray-900">Admin Panel</h1>
						<div className="w-9" /> {/* Spacer for centering */}
					</div>
				</div>

				{/* Page content - Scrollable area */}
				<main className="flex-1 overflow-y-auto">{children}</main>
			</div>
		</div>
	);
};

export default Layout;
